import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from "lucide-react";
import { COMPANY, CITIES, SERVICES } from "@/lib/data";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-navy text-white">
      <div className="container-pad mx-auto max-w-7xl py-20">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-full bg-white/5 overflow-hidden ring-1 ring-white/10">
                <img src={COMPANY.logoUrl} alt="Comfort Care" className="w-full h-full object-cover" />
              </div>
              <div className="font-heading text-lg font-semibold">Comfort Care</div>
            </div>
            <p className="text-white/70 leading-relaxed text-sm">
              Premium non-emergency medical transportation across Virginia, Washington DC, and Maryland. Safe, reliable, and compassionate every mile.
            </p>
            <div className="flex gap-3 mt-6">
              <a href={COMPANY.googleProfileUrl} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/5 hover:bg-teal-500 transition flex items-center justify-center" aria-label="Google Business Profile" title="Find us on Google">
                <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                </svg>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/5 hover:bg-teal-500 transition flex items-center justify-center" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/5 hover:bg-teal-500 transition flex items-center justify-center" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-sm uppercase tracking-[0.2em] text-teal-300 mb-5">Services</h4>
            <ul className="space-y-2.5 text-sm text-white/75">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="hover:text-teal-300 transition">{s.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm uppercase tracking-[0.2em] text-teal-300 mb-5">Service Areas</h4>
            <div className="text-xs uppercase tracking-widest text-teal-300/70 mb-2">VA · DC · MD</div>
            <ul className="space-y-2.5 text-sm text-white/75">
              {CITIES.map((c) => (
                <li key={c.slug}>
                  <Link to={`/service-area/${c.slug}`} className="hover:text-teal-300 transition">
                    {c.name} Medical Transportation
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm uppercase tracking-[0.2em] text-teal-300 mb-5">Contact</h4>
            <ul className="space-y-3.5 text-sm text-white/80">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-teal-300 mt-0.5 shrink-0" />
                <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-teal-300">{COMPANY.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-teal-300 mt-0.5 shrink-0" />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-teal-300 break-all">{COMPANY.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-teal-300 mt-0.5 shrink-0" />
                <span>{COMPANY.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-teal-300 mt-0.5 shrink-0" />
                <span>{COMPANY.hours}</span>
              </li>
            </ul>
          </div>
        </div>

          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/50">
            <div>© {new Date().getFullYear()} Comfort Care Transportation. All rights reserved.</div>
            <div className="flex gap-6">
              <Link to="/careers" className="hover:text-teal-300">Careers</Link>
              <a href="#" className="hover:text-teal-300">Privacy Policy</a>
              <a href="#" className="hover:text-teal-300">Terms of Service</a>
              <Link to="/admin" className="hover:text-teal-300">Admin</Link>
            </div>
          </div>
      </div>
    </footer>
  );
}
